import React from "react";
import PropTypes from "prop-types";

// ─── Fallback UI when a component crashes ────────────────────────────────
function ErrorFallback({ error, resetErrorBoundary, componentName }) {
  return (
    <div className="error-boundary-fallback">
      <div className="error-boundary-card">
        <div className="error-boundary-icon">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#EF4444"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h2 className="error-boundary-title">Something went wrong</h2>

        {componentName && (
          <p className="error-boundary-component">
            Error in: <strong>{componentName}</strong>
          </p>
        )}

        <p className="error-boundary-message">
          {error?.message || "An unexpected error occurred."}
        </p>

        <div className="error-boundary-actions">
          <button
            className="error-boundary-btn error-boundary-btn--primary"
            onClick={resetErrorBoundary}
          >
            Try Again
          </button>
          <button
            className="error-boundary-btn error-boundary-btn--secondary"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </button>
        </div>

        {process.env.NODE_ENV === "development" && error?.stack && (
          <details className="error-boundary-stack">
            <summary>Stack Trace</summary>
            <pre>{error.stack}</pre>
          </details>
        )}
      </div>
    </div>
  );
}

ErrorFallback.propTypes = {
  error: PropTypes.object,
  resetErrorBoundary: PropTypes.func,
  componentName: PropTypes.string,
};

// ─── Error Boundary Class Component ───────────────────────────────────────
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console in development
    console.error(
      `[ErrorBoundary] ${this.props.componentName || "Unknown Component"}:`,
      error,
      errorInfo,
    );

    this.setState({ errorInfo });

    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    if (process.env.NODE_ENV === "production") {
      // Example: Sentry.captureException(error, { extra: errorInfo });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          resetErrorBoundary: this.handleReset,
        });
      }

      // Otherwise use default fallback
      return (
        <ErrorFallback
          error={this.state.error}
          resetErrorBoundary={this.handleReset}
          componentName={this.props.componentName}
        />
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  componentName: PropTypes.string,
  fallback: PropTypes.func,
  onReset: PropTypes.func,
};

// ─── HOC to wrap any component with an error boundary ────────────────────
export function withErrorBoundary(WrappedComponent, componentName) {
  return function WithErrorBoundaryWrapper(props) {
    return (
      <ErrorBoundary
        componentName={
          componentName || WrappedComponent.displayName || WrappedComponent.name
        }
      >
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
